import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { SUB_LABEL, INVESTMENT_TYPES, fmt } from "./constants";
import UndoToBusinessButton from "./UndoToBusinessButton";
import BiSelect from "@/components/BiSelect";
import BiDatePicker from "@/components/BiDatePicker";
import { formatDateShort } from "@/shared/format";

/**
 * Investments tab — dividends, franking credits, interest income, distributions.
 * Owned by <PersonalTaxPage /> which passes entries filtered to type in
 * {dividend, franking_credit} + onChange refresh callback.
 */
export default function InvestmentsTab({ entries, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    kind: "dividend|dividend",
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    payer: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!Number(form.amount)) { toast.error("Amount required"); return; }
    const [type, sub] = form.kind.split("|");
    setBusy(true);
    try {
      await api.post("/personal-tax/entries", {
        type, sub_category: sub, amount: Number(form.amount),
        date: form.date, payer: form.payer, notes: form.notes, source: "manual",
      });
      toast.success("Investment added");
      setShowAdd(false);
      setForm({ kind: "dividend|dividend", amount: "", date: new Date().toISOString().slice(0, 10), payer: "", notes: "" });
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const removeEntry = async (eid) => {
    try {
      await api.delete(`/personal-tax/entries/${eid}`);
      await onChange();
    } catch { toast.error("Could not delete"); }
  };

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [entries]
  );

  return (
    <div className="space-y-3" data-testid="investments-tab">
      {entries.length === 0 && (
        <div className="bi-surface p-4 text-center text-sm text-white/60">
          No dividends or franking credits yet for this FY.
        </div>
      )}

      {sortedEntries.map((e) => (
        <div key={e.id} className="bi-surface p-3 flex items-center gap-2" data-testid={`inv-entry-${e.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">
              {SUB_LABEL[e.sub_category] || e.type}
              {e.payer && <span className="text-white/50 font-normal"> · {e.payer}</span>}
            </div>
            <div className="text-[10px] text-white/40">{formatDateShort(e.date)}{e.notes ? ` · ${e.notes}` : ""}</div>
          </div>
          <div className="tabular-nums font-bold text-emerald-300">{fmt(e.amount)}</div>
          <UndoToBusinessButton entry={e} onDone={onChange} testIdSuffix={`inv-${e.id}`} />
          <button
            data-testid={`inv-delete-${e.id}`}
            onClick={() => removeEntry(e.id)}
            className="w-8 h-8 rounded-lg text-white/30 hover:text-red-400 inline-flex items-center justify-center"
          ><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}

      {showAdd ? (
        <div className="bi-surface p-4 space-y-2" data-testid="add-investment-form">
          <div className="font-heading font-bold text-sm">Add investment income</div>
          <BiSelect
            data-testid="inv-kind"
            className="w-full bi-input"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
          >
            {INVESTMENT_TYPES.map((t) => (
              <option key={`${t.type}|${t.sub}`} value={`${t.type}|${t.sub}`}>{t.label}</option>
            ))}
          </BiSelect>
          <input
            data-testid="inv-amount"
            className="w-full bi-input"
            type="number" step="0.01"
            placeholder="Amount (AUD)"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
          <input
            data-testid="inv-payer"
            className="w-full bi-input"
            placeholder="Payer (e.g. CBA)"
            value={form.payer}
            onChange={(e) => setForm({ ...form, payer: e.target.value })}
          />
          <BiDatePicker
            data-testid="inv-date"
            className="w-full bi-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          <input
            data-testid="inv-notes"
            className="w-full bi-input"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="flex gap-2">
            <button
              data-testid="inv-save"
              onClick={save}
              disabled={busy}
              className="flex-1 h-11 rounded-xl bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="h-11 px-4 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider"
            >Cancel</button>
          </div>
        </div>
      ) : (
        <button
          data-testid="add-investment"
          onClick={() => setShowAdd(true)}
          className="w-full h-11 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Add dividend / franking credit
        </button>
      )}
    </div>
  );
}
